/**
 * Rock UCS (No Strain) — frontend mirror of pure calculation helpers.
 *
 * MUST stay in sync with backend/src/calculation/rockUcsNsCalculations.ts
 * and backend/src/calculation/rockUcsNsConformity.ts — the backend is the
 * source of truth on save; this mirror exists only for live preview while
 * typing. (Same convention as rock-ucs/computations.ts.)
 *
 * SYNC-POINTER: backend/src/calculation/rockUcsNsCalculations.ts
 */
import type { RockUCSNSSpecimen } from '../../../types/rockUcsNs';

// ── ASTM D7012 Method C stress-rate band (mirrors rockUcsNs.ts constants) ────
// Min = 0.5 MPa/s, Max = 1.0 MPa/s → in kN/s per specimen area.
export const UCSNS_MIN_STRESS_RATE_MPAS = 0.5;
export const UCSNS_MAX_STRESS_RATE_MPAS = 1.0;

// ── Geometry thresholds (ASTM D7012) ─────────────────────────────────────────
export const UCSNS_LD_RATIO_MIN = 2.0;
export const UCSNS_LD_RATIO_MAX = 2.5;

// ── Helper: average of non-null values (mirrors backend average()) ────────────
function average(values: (number | null | undefined)[]): number | null {
  const nums = values.filter((v): v is number => v !== null && v !== undefined && Number.isFinite(v));
  return nums.length ? nums.reduce((s, v) => s + v, 0) / nums.length : null;
}

// ── Specimen preview (mirrors computeSpecimen() in rockUcsNsCalculations.ts) ─

export interface UcsNsSpecimenPreview {
  avgDiameterMm: number | null;
  avgLengthMm: number | null;
  lOverD: number | null;
  lOverDPass: boolean | null;
  areaMm2: number | null;
  volumeMl: number | null;
  bulkDensityGCm3: number | null;
  /** Derived stress rate from rateOfLoadingKNs and area (MPa/s). */
  stressRateMPaS: number | null;
  /** ASTM loading-rate band lower bound (kN/s) for this specimen. */
  minRateKNs: number | null;
  /** ASTM loading-rate band upper bound (kN/s) for this specimen. */
  maxRateKNs: number | null;
  /** Whether the entered rate is outside the ASTM band. */
  rateOutsideAstmRange: boolean | null;
  /** Derived UCS from maxLoadKN and area (MPa). */
  computedUCSMPa: number | null;
}

/**
 * Compute live preview for one specimen.
 *
 * Mirrors computeSpecimen() in rockUcsNsCalculations.ts:
 *   avgDia = AVERAGE(diameter1..6)
 *   avgLen = AVERAGE(length1..4)
 *   area   = (avgDia/2)^2 * PI
 *   vol    = area * avgLen / 1000   (mL)
 *   dens   = massG / vol
 *   stressRate = rateOfLoadingKNs / area * 1000   (MPa/s, cell K39)
 *   UCS    = maxLoadKN / area * 1000              (MPa)
 *   min kN/s = area * 0.5 / 1000
 *   max kN/s = area * 1.0 / 1000
 */
export function computeUcsNsSpecimenPreview(s: RockUCSNSSpecimen): UcsNsSpecimenPreview {
  const avgDiameterMm = average([s.diameter1, s.diameter2, s.diameter3, s.diameter4, s.diameter5, s.diameter6]);
  const avgLengthMm = average([s.length1, s.length2, s.length3, s.length4]);

  const areaMm2 = avgDiameterMm !== null ? Math.PI * (avgDiameterMm / 2) ** 2 : null;
  const volumeMl = areaMm2 !== null && avgLengthMm !== null ? (areaMm2 * avgLengthMm) / 1000 : null;
  const bulkDensityGCm3 =
    volumeMl !== null && volumeMl > 0 && s.massG !== null && s.massG !== undefined
      ? s.massG / volumeMl
      : null;

  const lOverD = avgDiameterMm && avgLengthMm ? avgLengthMm / avgDiameterMm : null;
  const lOverDPass =
    lOverD === null
      ? null
      : lOverD >= UCSNS_LD_RATIO_MIN && lOverD <= UCSNS_LD_RATIO_MAX;

  // Loading-rate band derived from area (matches backend computeLoadingRate())
  const minRateKNs = areaMm2 !== null ? (areaMm2 * UCSNS_MIN_STRESS_RATE_MPAS) / 1000 : null;
  const maxRateKNs = areaMm2 !== null ? (areaMm2 * UCSNS_MAX_STRESS_RATE_MPAS) / 1000 : null;

  // Stress rate: K39 formula = K38 / V31 * 1000 = rateKNs / areaMm2 * 1000
  const stressRateMPaS =
    s.rateOfLoadingKNs !== null && s.rateOfLoadingKNs !== undefined && areaMm2 !== null && areaMm2 > 0
      ? (s.rateOfLoadingKNs / areaMm2) * 1000
      : null;

  const rateOutsideAstmRange =
    s.rateOfLoadingKNs !== null && minRateKNs !== null && maxRateKNs !== null
      ? s.rateOfLoadingKNs < minRateKNs || s.rateOfLoadingKNs > maxRateKNs
      : null;

  // UCS: T39 formula = T37 / V31 * 1000 = maxLoadKN / areaMm2 * 1000
  const computedUCSMPa =
    s.maxLoadKN !== null && s.maxLoadKN !== undefined && areaMm2 !== null && areaMm2 > 0
      ? (s.maxLoadKN / areaMm2) * 1000
      : null;

  return {
    avgDiameterMm,
    avgLengthMm,
    lOverD,
    lOverDPass,
    areaMm2,
    volumeMl,
    bulkDensityGCm3,
    stressRateMPaS,
    minRateKNs,
    maxRateKNs,
    rateOutsideAstmRange,
    computedUCSMPa,
  };
}
